import { AxiosResponse } from 'axios'
import { useRouter } from 'next/router'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useSWRConfig } from 'swr'

import { useFetch } from '@/lib/hooks'
import {
  api,
  getCookie,
  getFingerprint,
  getUrlString,
  removeCookie,
  removeSession,
  setCookie
} from '@/lib/utils'
import {
  SiteProfile as SiteProfileType,
  UserMe as UserMeType,
  UserPermissions as UserPermissionsType
} from '@/types'

type Credentials = {
  login: string
  password: string
}

interface ContextInterface {
  login: (credentials: Credentials) => void
  loginOneTimeToken: (token: string) => Promise<AxiosResponse>
  logout: () => void
  forgotPassword: (params: {
    email: string
    callbackUrl?: string | null
    deliveryChannel?: 'email' | 'phone'
  }) => Promise<AxiosResponse>
  resetPassword: (password: string, uidb64: string, token: string) => Promise<AxiosResponse>
  verifyToken: () => boolean
  isAuthenticated: boolean | null
  isEmailConfirmed?: boolean
  isValidating: boolean
  userData: UserMeType
  siteProfileData: SiteProfileType
  permissionsData: UserPermissionsType
  updateToken: (response: AxiosResponse) => void
  setEmailConfirmed: () => void
}

const AuthContext = createContext<ContextInterface | null>(null)

interface Props {
  children: any
}

export const useAuth = () => useContext(AuthContext)

function useProvideAuth() {
  const { cache }: { cache: any } = useSWRConfig()
  const router = useRouter()
  const { site: domain, token: sesameToken } = router.query || {}

  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)

  const { token: cookieToken, expiry: cookieExpiry } = getCookie('eskolare-shop-token') || {}

  const {
    data: userData,
    isValidating: userIsValidating,
    mutate: userMutate
  } = useFetch([isAuthenticated && domain ? `/${domain}/accounts/me/` : null])

  const {
    data: siteProfileData,
    isValidating: siteProfileIsValidating,
    mutate: siteProfileMutate
  } = useFetch([isAuthenticated && domain ? `/${domain}/accounts/profile/` : null])

  const {
    data: permissionsData,
    isValidating: permissionsIsValidating,
    mutate: permissionsMutate
  } = useFetch([isAuthenticated && domain ? `/${domain}/accounts/permissions/` : null])

  const handleUpdateToken = useCallback(
    (response: AxiosResponse) => {
      const { data, status } = response || {}
      if (status === 200) {
        const tokenData = { expiry: data?.expiry, token: data?.token }
        setCookie('eskolare-shop-token', tokenData)
        setIsAuthenticated(true)
        userMutate?.()
        siteProfileMutate?.()
        permissionsMutate?.()
      }
    },
    [userMutate, siteProfileMutate, permissionsMutate]
  )

  const handleSetEmailConfirmed = useCallback(() => {
    siteProfileMutate?.(
      (data: SiteProfileType) => (data ? { ...data, isEmailConfirmed: true } : data),
      { revalidate: false }
    )
  }, [siteProfileMutate])

  const login = async (credentials: Credentials) => {
    const fingerprint = await getFingerprint()
    const response = await api.post(
      `/${domain}/accounts/login/`,
      { fingerprint },
      {
        headers: {
          Authorization: `Basic ${Buffer.from(
            `${credentials.login}:${credentials.password}`
          ).toString('base64')}`
        }
      }
    )
    handleUpdateToken(response)
    return response
  }

  const loginOneTimeToken = async (oneTimeToken: string) => {
    const fingerprint = await getFingerprint()
    const response = await api.post(
      `/${domain}/accounts/login/`,
      { fingerprint },
      { headers: { Authorization: `OneTimeToken ${oneTimeToken}` } }
    )
    handleUpdateToken(response)
    return response
  }

  const loginSesame = useCallback(async () => {
    const tokenSesame = getUrlString(sesameToken)
    const fingerprint = await getFingerprint()
    const response = await api.post(`/${domain}/accounts/login/?sesame=${tokenSesame}`, {
      fingerprint
    })

    handleUpdateToken(response)
    return response
  }, [domain, handleUpdateToken, sesameToken])

  const logout = async () => {
    try {
      await api.post(`/${domain}/accounts/logout/`)
    } finally {
      removeCookie('eskolare-shop-token')
      removeSession('stuid')
      setIsAuthenticated(false)
    }
  }

  const forgotPassword = async ({
    email,
    callbackUrl = null,
    deliveryChannel
  }: {
    email: string
    callbackUrl?: string | null
    deliveryChannel?: 'email' | 'phone'
  }) => {
    const response = await api.post(`/${domain}/password-reset/`, {
      email,
      ...(callbackUrl ? { callbackUrl } : {}),
      ...(deliveryChannel ? { deliveryChannel } : {})
    })
    return response
  }

  const resetPassword = async (password: string, uidb64: string, hash: string) => {
    const response = await api.post(`/${domain}/password-reset/confirm/`, {
      password,
      uidb64,
      token: hash
    })
    return response
  }

  const verifyToken = useCallback(() => {
    let expired = true
    if (cookieToken) {
      const expiryDate = new Date(cookieExpiry)
      const nowDate = new Date()

      expired = nowDate.getTime() > expiryDate.getTime()

      if (expired) {
        removeCookie('eskolare-shop-token')
        removeSession('stuid')
      }
    }

    return !expired
  }, [cookieToken, cookieExpiry])

  // Effects
  useEffect(() => {
    const isValidToken = verifyToken()
    setIsAuthenticated(!!isValidToken)
  }, [verifyToken, cookieToken])

  useEffect(() => {
    if (sesameToken) {
      if (!isAuthenticated) {
        loginSesame()
      } else if (window) {
        const params = new URL(window.location.href)?.searchParams
        params.delete('token')
        router.push({ pathname: window.location.pathname, query: params.toString() }, undefined, {
          shallow: false
        })
      }
    }
  }, [isAuthenticated, loginSesame, router, sesameToken])

  // Clear all SWR cache (experimental)
  useEffect(() => {
    cache?.clear?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  return {
    login,
    logout,
    loginOneTimeToken,
    forgotPassword,
    resetPassword,
    verifyToken,
    isAuthenticated,
    isValidating: userIsValidating || siteProfileIsValidating || permissionsIsValidating,
    userData,
    siteProfileData,
    isEmailConfirmed: siteProfileData?.isEmailConfirmed,
    permissionsData,
    updateToken: handleUpdateToken,
    setEmailConfirmed: handleSetEmailConfirmed
  }
}

export default function AuthProvider({ children }: Props) {
  const auth = useProvideAuth()
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>
}
